import { ApplicationRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ProjectService } from '../../services/project.service';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { MODAL, ModalService } from '../../services/modal.service';
import { CustomerService } from '../../services/customer.service';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentService } from '../../services/current.service';
import { HbmIframeService } from '../../services/hbm/hbm-iframe.service';
import { Project } from 'src/app/models/project';
import { GTM } from 'src/app/services/gtm.service';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { DatasService } from 'src/app/services/datas.service';
import { WorkbenchService } from 'src/app/services/configurators-2d/applications/workbench.service';
import { SearchPatternTypeEnum } from 'src/app/enum/kitchen/portal/project/search-pattern-type.enum';
import { StorageService } from 'src/app/services/configurators-2d/applications/storage.service';
import { Subscribable } from '../../stores/tunnel/model/subscribable';
import { TunnelDataStore } from '../../stores/tunnel/tunnel-data.store';
import { CustomerState } from '../../stores/tunnel/model/tunnel-data-state';
import { TranslateService } from '@ngx-translate/core';
import { PrintService } from 'src/app/services/print.service';
import { logError } from 'src/app/services/helpers/sentry.helper';

@Component({
  selector: 'ng-casto-project',
  templateUrl: './project.component.html',
})
export class ProjectComponent extends Subscribable implements OnInit, OnDestroy {
  projectData: any;
  private readonly _routeChangeSubscription: Subscription;
  noCache: string;
  mainImageUrl: string;
  vendorContext: boolean;
  isworkbenchProject: boolean = false;
  isLegacy: boolean;
  isQuoteAndOrderLoading: boolean;
  private projectId: string;
  private workbenchProjectUUID: string;
  private currentCustomer: CustomerState;
  private currentConfigurationData: Map<CONFIG_CONTEXT, unknown>;

  constructor(
    public project: ProjectService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public modal: ModalService,
    public customer: CustomerService,
    public appRef: ApplicationRef,
    public byMe: HbmIframeService,
    public gtm: GTM,
    public current: CurrentService,
    public kitchen: KitchenService,
    public storageService: StorageService,
    public workbench: WorkbenchService,
    public tunnelService: TunnelService,
    public translate: TranslateService,
    public print: PrintService,
    public injector: Injector,
    private readonly datas: DatasService,
    private readonly tunnelDataStore: TunnelDataStore
  ) {
    super();
    this.vendorContext = environment.vendor;
    this._routeChangeSubscription = this.router.events.subscribe(async (event: Event) => {
      if (event instanceof NavigationEnd) {
        this.projectId = this.route.snapshot.params['projectUUID'];
        await this.loadProject(this.projectId);
      }
    });
  }

  /** Client du tunnel vendeur, sinon client connecté (CIAM) */
  private get owner() {
    return this.currentCustomer?.userData ?? this.customer.userDatas;
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.currentConfigurationData = configurationData;
        }
      })
    );
  }

  private async loadStorageConfiguration(HBMConfigurationId: string): Promise<void> {
    this.customer.context = CONFIG_CONTEXT.STORAGE;
    await this.storageService.loadStorageConfigurationFromHBM(HBMConfigurationId);
  }

  private async loadKitchenConfiguration(HBMConfigurationId: string): Promise<void> {
    this.customer.context = CONFIG_CONTEXT.KITCHEN;
    let result = await this.kitchen.loadKitchenConfigurationFromHBM(HBMConfigurationId);
    this.isworkbenchProject = result?.workbenchConfigurationId != undefined && result?.workbenchConfigurationId != '';
    this.workbenchProjectUUID = result?.workbenchConfigurationId;
  }

  async loadProject(projectUUID: string): Promise<void> {
    this.modal.isLoading = true;
    this.project.getDetails(projectUUID).then(async (projectData: any) => {
      this.projectData = projectData;
      //faire pareil pour storage
      switch (this.projectData.distributionID) {
        case environment.byMe.kitchen.distribID:
          await this.loadKitchenConfiguration(projectUUID);
          break;
        case environment.byMe.storage.distribID:
          await this.loadStorageConfiguration(projectUUID);
          break;
      }
      this.isLegacy = this.projectData.userID !== this.owner?.id;
      this.noCache = projectData.dateUpdate.replace(/[\W]+/g, '');

      if (this.projectData.medias.renderHQ.length > 0) {
        // always select latest realistic images as overview image
        this.mainImageUrl = this.projectData.medias.renderHQ[this.projectData.medias.renderHQ.length - 1].url;
      } else if (this.projectData.medias.thumbnail.length > 1) {
        // always use the second thumbnail images as overview image, as it has better resolution
        this.mainImageUrl = this.projectData.medias.thumbnail[1].url;
      } else {
        // if there is not realistic image or thumbnail, we use a place holder image as overview image
        this.mainImageUrl = 'assets/img/placeholders/project@2x.png';
      }

      this.modal.isLoading = false;
      this.appRef.tick();
    });
  }

  async openQuotation(): Promise<void> {
    this.modal.isLoading = true;

    try {
      await this.openQuotationByContext();
    } catch (e) {
      logError(e);
      alert('Une erreur est survenue, le devis ne peut pas être généré.');
    } finally {
      this.modal.isLoading = false;
    }
  }

  async openQuotationByContext(): Promise<void> {
    const currentContext = this.customer.context;
    this.customer.context = CONFIG_CONTEXT.WORKBENCH;
    let project = await this.workbench.getQuotationForKitchen(this.workbenchProjectUUID);
    const thumb = await this.workbench.getThumbById(this.workbenchProjectUUID);

    if (project.categories.some(c => c.products.some(p => p.vat))) {
      //S'il s'agit d'un devis avec plusieurs Tva
      this.modal.display(MODAL.MULTI_VAT_QUOTE, {
        project: project,
        context: this.customer.context,
        thumb: await this.resizeImage(thumb.image, 3000, 2250),
      });
    } else {
      project.percentTVA = project.percentTVA != 0 && project.percentTVA != undefined ? project.percentTVA : 0.2;
      let imgPdfBase64 = await this.workbench.generateNewPdf({
        configContext: this.customer.context,
        configurationId2D: this.workbench.configuration.id,
        customerContext: this.translate.instant('popins.products_list.print_title_' + this.customer.context),
        configThumbnail: await this.resizeImage(thumb.image, 3000, 2250),
        disclaimer: this.getDisclaimersContext(this.customer.context),
        quotation: project,
        storeName: this.current.vendor ? this.current.vendor.store.name : 'WEB',
      });
      this.print.printPdf(imgPdfBase64.value, !!this.current.vendor);
    }

    this.customer.context = currentContext;
  }

  private getDisclaimersContext(context: string): string {
    return context === CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM
      ? this.translate.instant('popins.products_list.aside.closetDoorsCustomDisclaimer')
      : this.translate.instant('popins.products_list.aside.disclaimerPdf');
  }

  resizeImage(uri, width, height): Promise<string> {
    return new Promise(function (resolve) {
      let img = document.createElement('img');
      img.onload = function () {
        let canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        let dataUri = canvas.toDataURL().toString();
        resolve(dataUri);
      };
      img.crossOrigin = '';
      img.src = uri;
    });
  }

  ngOnDestroy(): void {
    this._routeChangeSubscription.unsubscribe();
    super.unsubscribe();
  }

  ngOnInit(): void {
    this.gtm.trackPage(this.router.url, `Mon projet | ${this.projectId}`, {
      projectId: this.projectId,
    });
    super.subscribe();
  }

  zoomImage(src: string): void {
    this.modal.display(MODAL.IMAGE_ZOOM, { imgUrl: src });
  }

  backToProjects() {
    this.modal.display(MODAL.PROJECTS, {
      customerId: this.owner?.id,
      defaultValue: {
        value: this.owner?.email,
        searchPatternType: SearchPatternTypeEnum.EMAIL,
      },
    });
    this.tunnelDataStore.resetState(false);
  }

  updateProperty(value: any, key: string) {
    let _cleanValue: string = value
      .split('<br>')
      .filter(l => l != '')
      .map(l => l.replace(/(\r\n|\n\r|\r|\n)/g, ''))
      .join('\n');

    switch (key) {
      case 'title':
        this.project
          .updateNameAndDescription(this.projectData.projectUUID, _cleanValue, this.projectData.description)
          .then(() => {
            this.projectData.projectName = _cleanValue;
            this.appRef.tick();
          });
        break;

      case 'description':
        this.project
          .updateNameAndDescription(this.projectData.projectUUID, this.projectData.projectName, _cleanValue)
          .then(() => {
            this.projectData.description = _cleanValue;
            this.appRef.tick();
          });
        break;

      case 'notes':
        this.project.updateManualNotes(this.projectData.projectUUID, _cleanValue).then(() => {
          this.projectData.manualNotes = _cleanValue;
          this.appRef.tick();
        });
        break;

      default:
        break;
    }
  }

  async editProject(project: any, event: any) {
    this.current.reset();
    event.stopPropagation();

    this.current.setHBMProject({
      id: project.projectID,
      uuid: project.projectUUID,
      name: project.projectName,
      description: project.description,
      locked: project.is_locked,
      isLegacy: this.isLegacy,
    });

    this.byMe.prepareBymeSettingsForLoadingProject();
    if (project.distributionID == environment.byMe.kitchen.distribID) {
      if (this.current.vendor) {
        this.customer.context = CONFIG_CONTEXT.KITCHEN;
        this.router.navigate(['tunnel', '1']);
      } else {
        this.router.navigate(['cuisine', 'configuration']);
      }
    } else if (project.distributionID == environment.byMe.storage.distribID) {
      if (this.current.vendor) {
        this.customer.context = CONFIG_CONTEXT.STORAGE;
        this.router.navigate(['tunnel', '1']);
      } else {
        this.router.navigate(['rangements', 'configuration']);
      }
    } else if (project.distributionID == environment.byMe.bathroom.distribID) {
      this.router.navigate(['salle-de-bain', 'configuration']);
    }
    this.modal.close();
  }

  duplicateProject(projectUUID: string, projectID: number, projectName: string, event: any) {
    event.stopPropagation();
    let project: Project = new Project();
    project.projectName = projectName;
    project.projectUUID = projectUUID;
    project.projectID = projectID;
    project.distributionID = this.projectData.distributionID;

    this.modal.display(MODAL.PROJECT_DUPLICATE, {
      project,
      onClose: (clonedProject: Project) => {
        if (clonedProject) {
          this.router.navigate(['projects', clonedProject.projectUUID]);
          this.modal.close();
        } else {
          this.modal.close();
        }
      },
    });
  }

  deleteProject(projectID: number, event: any) {
    event.stopPropagation();
    let project: Project = new Project();
    project.projectID = projectID;
    // Réassignation des valeurs du projet pour la suppression de la config kitchen dans la modal PROJECT_DELETE
    project.projectUUID = this.projectId;
    project.distributionID = this.projectData.distributionID;
    if (this.isLegacy) project.extraDatas.isLegacy = true;
    this.modal.display(MODAL.PROJECT_DELETE, {
      project,
      onClose: projectDeleted => {
        if (projectDeleted) {
          this.router.navigate(['']);
          this.modal.display(MODAL.PROJECTS, {
            customerId: this.owner?.id,
          });
        } else {
          this.modal.close();
        }
      },
    });
  }

  lockProject(projectID: number, event: any) {
    event.stopPropagation();
    this.project.lock(projectID).then(
      () => {
        this.projectData.is_locked = true;
      },
      error => {
        logError(error);
      }
    );
  }

  unlockProject(projectID: number, event: any) {
    event.stopPropagation();
    this.project.unlock(projectID).then(
      () => {
        this.projectData.is_locked = false;
      },
      error => {
        logError(error);
      }
    );
  }
}
